import React, { useMemo, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

const BOX = 'relative w-full min-h-9 rounded-md pl-2 pr-7 py-1 flex flex-wrap items-center gap-1 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] focus-within:border-[#FF451A]/60 transition-colors';
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

// Type-to-filter multi-select with removable chips; empty selection means "Any".
export const MultiSelect = ({ value, onChange, options, placeholder = 'Any', clearLabel = 'Clear all (Any)', testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return options.filter((o) => !value.includes(o) && (!s || o.toLowerCase().includes(s))).slice(0, 200);
  }, [q, options, value]);

  const add = (v) => { onChange([...value, v]); setQ(''); };
  const remove = (v) => onChange(value.filter((x) => x !== v));
  const onKey = (e) => {
    if (e.key === 'Backspace' && !q && value.length) remove(value[value.length - 1]);
    if (e.key === 'Enter') { e.preventDefault(); if (open && filtered.length) add(filtered[0]); }
  };

  return (
    <div className="relative">
      <div className={BOX} data-testid={`${testid}-box`}>
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full bg-[#FF451A]/10 text-[#FF451A] text-[11px] font-medium pl-2 pr-1 py-0.5" data-testid={`${testid}-chip-${slug(v)}`}>
            {v}
            <button type="button" onClick={() => remove(v)} className="rounded-full hover:bg-[#FF451A]/20 p-0.5" data-testid={`${testid}-chip-remove-${slug(v)}`}><X size={10} /></button>
          </span>
        ))}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKey}
          placeholder={value.length ? '' : placeholder}
          className="flex-1 min-w-[50px] h-7 bg-transparent outline-none text-sm text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
          data-testid={testid}
        />
        <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-black/[0.12] dark:border-white/[0.08] bg-white dark:bg-[#1A1D24] shadow-lg" data-testid={`${testid}-options`}>
          {value.length > 0 && <button type="button" onMouseDown={() => onChange([])} className="w-full text-left px-3 py-1.5 text-xs font-medium text-[#6B6B6B] dark:text-zinc-400 hover:bg-black/[0.04] dark:hover:bg-white/5" data-testid={`${testid}-clear`}>{clearLabel}</button>}
          {filtered.map((o) => (
            <button type="button" key={o} onMouseDown={(e) => { e.preventDefault(); add(o); }} className="w-full text-left px-3 py-1.5 text-sm text-[#0F0F0F] dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/5" data-testid={`${testid}-option`}>
              {o}
            </button>
          ))}
          {!filtered.length && <div className="px-3 py-2 text-xs text-[#6B6B6B] dark:text-zinc-400">No matches</div>}
        </div>
      )}
    </div>
  );
};
